import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { auth, firestore } from '../../../../../../libs/firebase-service/src';
import { setCart, setUser } from '../../../../../../libs/redux-slices/src';
import { fetchData, handleToaster } from '../../Utils/UtilsFunctions';
import { environment } from '../../../environments/environment';
import { useTranslation } from 'react-i18next';

export const useOrderActions = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const user = useSelector((state) => state.authReducer.userData);

  const handleReorder = useCallback(
    async (order) => {
      try {
        const { order_items, store } = order;

        const updatedUser = {
          ...user,
          bagItems: order_items,
          defaultStore: store.id,
        };

        // Clear current cart
        await firestore
          .collection('users')
          .doc(auth?.currentUser?.uid)
          .set({ bagItems: [] }, { merge: true });
        dispatch(setCart([]));

        // Set new items
        await firestore.collection('users').doc(auth?.currentUser?.uid).set(
          {
            bagItems: order_items,
            defaultStore: store.id,
          },
          { merge: true }
        );

        dispatch(setCart(order_items));
        dispatch(setUser({ userData: updatedUser }));

        handleToaster('success', 'Items added to cart successfully!');
      } catch (error) {
        console.error('Error reordering:', error);
        handleToaster('error', 'Failed to reorder items');
      }
    },
    [user, dispatch]
  );

  const handleCancelOrder = useCallback(
    async (orderId, onSuccess) => {
      try {
        const token = await auth?.currentUser?.getIdToken();
        const headers = {
          'Content-Type': 'application/json',
          Authorization: token,
        };

        await fetchData(
          `${environment?.apiUrl}/cancelOrderV2?restaurantId=${environment.restaurantId}`,
          'post',
          {
            orderId: orderId,
            reason: 'cancel order',
            typeCancel: 'Customer',
            uid: auth?.currentUser?.uid,
          },
          headers
        );

        handleToaster('success', t('profilee.success'));
        onSuccess?.();
      } catch (error) {
        if (error.response?.data?.message?.includes('approved')) {
          handleToaster('error', t('profilee.ApprovedBefore'));
        } else {
          handleToaster(
            'error',
            error.response?.data?.message || t('profilee.error')
          );
        }
      }
    },
    [t]
  );

  const handleRateOrder = useCallback(async ({ orderId, rating, feedback }) => {
    try {
      const token = await auth?.currentUser?.getIdToken();
      const headers = {
        'Content-Type': 'application/json',
        Authorization: token,
      };

      await fetchData(
        `${environment?.apiUrl}/review?restaurantId=${environment.restaurantId}`,
        'post',
        {
          orderId: orderId,
          text: feedback,
          rating: rating,
        },
        headers
      );

      handleToaster('success', 'Rating submitted successfully');
    } catch (error) {
      handleToaster(
        'error',
        error.response?.data?.message || 'Failed to submit rating'
      );
    }
  }, []);

  return {
    handleReorder,
    handleCancelOrder,
    handleRateOrder,
  };
};

